<template>
  <div class="pa-admin rl-admin">
    <div class="paa-main">
      <div v-if="loadError" class="paa-error">{{ loadError }}</div>
      <div v-if="state.settingsLoaded === false" class="paa-error">
        This web process could not read the stored settings (table missing or database down at start): the numbers in
        the code apply until a restart after the migration. Nothing saved here takes effect in the meantime.
      </div>
      <div v-if="notice" class="rl-notice" :class="{ 'rl-notice--bad': noticeIsError }">{{ notice }}</div>

      <div class="paa-card rl-card">
        <div class="rl-card__head">
          <h3>Modes</h3>
          <button type="button" class="paa-btn paa-btn--secondary" :disabled="loading" @click="load">Refresh</button>
        </div>
        <div class="rl-modes">
          <div class="rl-mode-tile">
            <rl-tip label="Rate limits" :lines="tips.rateLimitsMode" class="paa-muted" />
            <span class="paa-badge" :class="'rl-mode--' + state.rateLimits.mode">{{ state.rateLimits.mode }}</span>
          </div>
          <div class="rl-mode-tile">
            <rl-tip label="Provider budgets" :lines="tips.budgetsMode" class="paa-muted" />
            <span class="paa-badge" :class="'rl-mode--' + state.budgets.mode">{{ state.budgets.mode }}</span>
          </div>
          <div class="rl-mode-tile">
            <rl-tip label="Budget alert at" :lines="tips.alertFraction" class="paa-muted" />
            <b>{{ Math.round(state.budgets.alertFraction * 100) }}% of a cap</b>
          </div>
        </div>
        <p class="paa-muted rl-small">
          The modes are a developer's decision, set in the server's <code>.env</code>: <b>log</b> counts and records what
          would be denied without denying anything, <b>enforce</b> denies, <b>off</b> counts nothing. The public per-visitor
          limits (login, signup, demo, coupons, payments) stay live in log mode. What you can change here are the
          numbers: a scope's limit, a silenced scope, a provider's daily cap.
          <span v-if="!canEdit">Changing them needs a special admin; you see everything read-only.</span>
          Never limited: {{ state.rateLimits.exemptEmails.join(', ') || 'nobody' }} and every admin (except on the
          admin-only scopes). Counters are per UTC day; today is {{ dayLabel(state.days.today) }}.
        </p>
      </div>

      <div class="paa-card rl-card">
        <div class="rl-card__head">
          <h3>Scopes</h3>
          <span class="paa-muted">{{ state.scopes.length }} scopes · "would" = denials log mode would have made, "deny" = real denials ·
            a silenced scope only logs while the mode enforces</span>
        </div>
        <div class="paa-tblwrap">
          <table class="paa-tbl rl-tbl">
            <thead>
              <tr>
                <th><rl-tip label="Scope" :lines="tips.scope" /></th>
                <th><rl-tip label="Routes" :lines="tips.routes" /></th>
                <th><rl-tip label="Per" :lines="tips.per" /></th>
                <th><rl-tip label="Default" :lines="tips.spec" /></th>
                <th><rl-tip label="Override" :lines="tips.override" /></th>
                <th><rl-tip label="Silenced" :lines="tips.silenced" /></th>
                <th><rl-tip label="Mode" :lines="tips.mode" /></th>
                <th><rl-tip label="Today" :lines="tips.today" /><span class="paa-sub">would / deny, bar vs the busiest scope</span></th>
                <th class="paa-r"><rl-tip label="Yesterday" :lines="tips.today" /><span class="paa-sub">would / deny</span></th>
                <th><rl-tip label="Last denial" :lines="tips.lastDenial" /></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in state.scopes" :key="row.scope" :class="{ 'rl-hot': row.today.deny || row.today.would }">
                <td>
                  <rl-tip :label="row.scope" :heading="row.scope" :lines="scopeLines(row)"><code>{{ row.scope }}</code></rl-tip>
                  <span v-if="!row.exemptStaff" class="paa-sub">admins included</span>
                </td>
                <td><rl-tip :label="String(row.routes.length)" heading="Endpoints" :lines="row.routes" /></td>
                <td><rl-tip :label="keyLabel(row.key)" :lines="tips.keys[row.key] || []" /></td>
                <td><code>{{ row.spec }}</code></td>
                <td>
                  <input v-model="scopeForms[row.scope].override" class="paa-ctl rl-ctl--spec"
                         :placeholder="row.spec" :disabled="!canEdit || saving">
                </td>
                <td class="rl-center">
                  <input v-model="scopeForms[row.scope].silenced" type="checkbox" :disabled="!canEdit || saving">
                </td>
                <td>
                  <rl-tip :label="row.mode" :heading="'Mode: ' + row.mode" :lines="tips.modes[row.mode] || []">
                    <span class="paa-badge" :class="'rl-mode--' + row.mode">{{ row.mode }}</span>
                  </rl-tip>
                </td>
                <td class="rl-barcell">
                  <div class="rl-bar" :title="`${row.today.would} would, ${row.today.deny} denied today`">
                    <span class="rl-bar__fill rl-bar__fill--would" :style="{ width: pct(row.today.would + row.today.deny, busiestScope) }"></span>
                    <span class="rl-bar__fill rl-bar__fill--deny" :style="{ width: pct(row.today.deny, busiestScope) }"></span>
                  </div>
                  <span class="rl-num">{{ row.today.would }} / {{ row.today.deny }}</span>
                </td>
                <td class="paa-r">{{ row.yesterday.would }} / {{ row.yesterday.deny }}</td>
                <td class="rl-small">
                  <template v-if="row.lastDenial">{{ row.lastDenial.subject }}<br>{{ row.lastDenial.at }}</template>
                  <span v-else class="paa-muted">—</span>
                </td>
                <td>
                  <button type="button" class="paa-btn paa-btn--primary paa-btn--sm" :disabled="!canEdit || saving || !scopeChanged(row)"
                          @click="saveScope(row)">Save</button>
                </td>
              </tr>
              <tr v-if="!state.scopes.length">
                <td class="paa-empty" colspan="11">{{ loading ? 'Loading…' : 'No scope registered.' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="paa-card rl-card">
        <div class="rl-card__head">
          <h3>Provider budgets</h3>
          <span class="paa-muted">platform-key calls per UTC day; own keys are never counted; empty cap = no cap</span>
        </div>
        <div class="paa-tblwrap">
          <table class="paa-tbl rl-tbl">
            <thead>
              <tr>
                <th><rl-tip label="Provider" :lines="tips.provider" /></th>
                <th><rl-tip label="Today" :lines="tips.providerToday" /><span class="paa-sub">calls / cap</span></th>
                <th class="paa-r">Yesterday</th>
                <th><rl-tip label="Cap per day" :lines="tips.cap" /></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in state.budgets.providers" :key="row.provider"
                  :class="{ 'rl-hot': row.cap && row.today >= row.cap }">
                <td>
                  <rl-tip :label="row.provider" :heading="row.provider" :lines="[row.description || '']"><code>{{ row.provider }}</code></rl-tip>
                </td>
                <td class="rl-barcell">
                  <div class="rl-bar" :class="barClass(row.today, row.cap)">
                    <span class="rl-bar__fill" :style="{ width: pct(row.today, row.cap) }"></span>
                  </div>
                  <span class="rl-num">{{ row.today }} / {{ row.cap || 'no cap' }}</span>
                </td>
                <td class="paa-r">{{ row.yesterday }}</td>
                <td>
                  <input v-model="providerForms[row.provider]" class="paa-ctl rl-ctl--short" placeholder="no cap"
                         :disabled="!canEdit || saving">
                </td>
                <td class="rl-actions">
                  <button type="button" class="paa-btn paa-btn--primary paa-btn--sm"
                          :disabled="!canEdit || saving || !providerChanged(row)" @click="saveProvider(row)">Save</button>
                  <button type="button" class="paa-btn paa-btn--secondary paa-btn--sm" :disabled="!canEdit || saving"
                          title="Clears today's counter and alert markers: lifts a tripped cap for the rest of the day"
                          @click="resetProvider(row.provider)">Reset today</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="paa-card rl-card">
        <div class="rl-card__head">
          <h3>Account buckets</h3>
          <span class="paa-muted">the active limit counters of one account, as caller and as owner</span>
        </div>
        <div class="rl-row">
          <label class="paa-fld">Account id
            <input v-model="accountId" class="paa-ctl rl-ctl--short" placeholder="12345" @keyup.enter="loadBuckets">
          </label>
          <button type="button" class="paa-btn paa-btn--primary" :disabled="!accountId || bucketsLoading" @click="loadBuckets">Load</button>
          <button type="button" class="paa-btn paa-btn--secondary" :disabled="!canEdit || !buckets.length || bucketsLoading"
                  @click="resetBuckets">Reset this account's buckets</button>
        </div>
        <div v-if="bucketsLoaded" class="paa-tblwrap">
          <table class="paa-tbl rl-tbl">
            <thead>
              <tr>
                <th><rl-tip label="Scope" :lines="tips.bucket" /></th>
                <th><rl-tip label="As" :lines="tips.per" /></th>
                <th>Window</th>
                <th>Used<span class="paa-sub">count / limit</span></th>
                <th class="paa-r">Resets in</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="bucket in buckets" :key="bucket.key" :class="{ 'rl-hot': bucket.limit && bucket.count >= bucket.limit }">
                <td><code :title="bucket.key">{{ bucket.scope }}</code></td>
                <td>{{ subjectLabel(bucket.subject) }}</td>
                <td>{{ bucket.window }}</td>
                <td class="rl-barcell">
                  <div class="rl-bar" :class="barClass(bucket.count, bucket.limit)">
                    <span class="rl-bar__fill" :style="{ width: pct(bucket.count, bucket.limit) }"></span>
                  </div>
                  <span class="rl-num">{{ bucket.count }} / {{ bucket.limit == null ? '?' : bucket.limit }}</span>
                </td>
                <td class="paa-r">{{ bucket.resetsIn }}s</td>
              </tr>
              <tr v-if="!buckets.length"><td class="paa-empty" colspan="5">No active bucket for account {{ bucketsAccountId }}.</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RlTip from './RlTip.vue'
import { RL_TIPS } from './rateLimitsHelp'

const EMPTY_STATE = {
  canEdit: false,
  rateLimits: { mode: '', exemptEmails: [] },
  scopes: [],
  budgets: { mode: '', alertFraction: 0.8, providers: [] },
  settings: [],
  days: { today: '', yesterday: '' },
  settingsLoaded: true,
}

/**
 * Admin > Manage > Rate Limits (views/openapi/admins/rate_limits_api.py). The modes come from the server's .env
 * and are shown read-only; the numbers a special admin may change live in the runtime_settings table
 * (core/runtime_settings.py): a scope's override spec, a silenced scope (log only while enforcing), a provider's
 * daily cap. Plus the denial counters per scope and one account's buckets. Other admins see the page read-only.
 */
export default {
  name: 'AdminRateLimits',
  components: { RlTip },
  data () {
    return {
      tips: RL_TIPS,
      loading: false,
      saving: false,
      loadError: '',
      notice: '',
      noticeIsError: false,
      state: EMPTY_STATE,
      scopeForms: {},
      providerForms: {},
      accountId: '',
      buckets: [],
      bucketsLoaded: false,
      bucketsLoading: false,
      bucketsAccountId: '',
    }
  },
  computed: {
    canEdit () {
      return !!this.state.canEdit
    },
    /** The highest would + deny count of the day across scopes; the scope bars are drawn against it. */
    busiestScope () {
      return Math.max(1, ...this.state.scopes.map((row) => row.today.would + row.today.deny))
    },
  },
  created () {
    this.load()
  },
  methods: {
    keyLabel (key) {
      return { user: 'caller', owner: 'owner', ip: 'visitor', custom: 'API key' }[key] || key
    },
    /** Tooltip of a scope name: what it covers, whose allowance it is, the limit in force. */
    scopeLines (row) {
      const lines = [row.description || 'No description yet.']
      const per = { user: 'Counts per person: each team member has their own allowance.',
        owner: 'Counts per account: an agency and its team members share one allowance.',
        ip: 'Counts per visitor address.', custom: 'Counts per partner API key.' }[row.key]
      if (per) lines.push(per)
      lines.push(`Limit in force: ${row.effectiveSpec}${row.override ? ' (overridden here, default ' + row.spec + ')' : ''}.`)
      if (!row.exemptStaff) lines.push('Admins are limited on this scope too.')
      return lines
    },
    subjectLabel (subject) {
      return String(subject || '').replace(/^user:/, 'caller ').replace(/^owner:/, 'owner ')
    },
    /** Width of a bar for x out of y, capped at 100%; no bar when y is unknown or 0. */
    pct (x, y) {
      if (!y || y <= 0) return '0%'
      return Math.min(100, Math.round((100 * (x || 0)) / y)) + '%'
    },
    barClass (x, y) {
      if (!y || y <= 0) return 'rl-bar--none'
      const ratio = (x || 0) / y
      return ratio >= 1 ? 'rl-bar--full' : ratio >= 0.8 ? 'rl-bar--warm' : ''
    },
    dayLabel (day) {
      return day ? `${day.slice(0, 4)}-${day.slice(4, 6)}-${day.slice(6, 8)}` : ''
    },
    applyState (data) {
      this.state = Object.assign({}, EMPTY_STATE, data)
      const scopeForms = {}
      for (const row of this.state.scopes) scopeForms[row.scope] = { override: row.override || '', silenced: !!row.silenced }
      this.scopeForms = scopeForms
      const providerForms = {}
      for (const row of this.state.budgets.providers) providerForms[row.provider] = row.cap ? String(row.cap) : ''
      this.providerForms = providerForms
    },
    scopeChanged (row) {
      const form = this.scopeForms[row.scope]
      return !!form && (form.override.trim() !== (row.override || '') || form.silenced !== !!row.silenced)
    },
    providerChanged (row) {
      const value = (this.providerForms[row.provider] || '').trim()
      return value !== (row.cap ? String(row.cap) : '')
    },
    failureText (error, fallback) {
      return (error && error.response && error.response.data && error.response.data.msg) || (error && error.message) || fallback
    },
    handle (promise, successText) {
      this.saving = true
      this.notice = ''
      return promise
        .then((response) => {
          const data = response && response.data
          if (!data || data.status !== 'SUCCESS') throw new Error((data && data.msg) || 'The change was refused.')
          this.applyState(data)
          this.notice = successText
          this.noticeIsError = false
        })
        .catch((error) => {
          this.notice = this.failureText(error, 'The change could not be saved.')
          this.noticeIsError = true
        })
        .finally(() => {
          this.saving = false
        })
    },
    load () {
      this.loading = true
      this.loadError = ''
      return this.$store.dispatch('getRateLimitsForAdmin')
        .then((response) => {
          const data = response && response.data
          if (!data || data.status !== 'SUCCESS') throw new Error((data && data.msg) || 'load failed')
          this.applyState(data)
        })
        .catch((error) => {
          this.loadError = this.failureText(error, 'We could not load the rate limits. Try again in a moment.')
        })
        .finally(() => {
          this.loading = false
        })
    },
    saveScope (row) {
      const form = this.scopeForms[row.scope]
      return this.handle(this.$store.dispatch('updateRateLimitScopeForAdmin', {
        scope: row.scope, body: { override: form.override.trim(), silenced: form.silenced },
      }), `${row.scope} saved.`)
    },
    saveProvider (row) {
      return this.handle(this.$store.dispatch('updateRateLimitProviderForAdmin', {
        provider: row.provider, body: { cap: (this.providerForms[row.provider] || '').trim() },
      }), `${row.provider} cap saved.`)
    },
    resetProvider (provider) {
      return this.handle(this.$store.dispatch('resetProviderBudgetForAdmin', { provider }), `Today's ${provider} counter cleared.`)
    },
    loadBuckets () {
      const accountId = parseInt(this.accountId, 10)
      if (!accountId) return
      this.bucketsLoading = true
      return this.$store.dispatch('getRateLimitBucketsForAdmin', { accountId })
        .then((response) => {
          const data = response && response.data
          if (!data || data.status !== 'SUCCESS') throw new Error((data && data.msg) || 'load failed')
          this.buckets = data.buckets || []
          this.bucketsAccountId = accountId
          this.bucketsLoaded = true
        })
        .catch((error) => {
          this.notice = this.failureText(error, 'We could not load the buckets.')
          this.noticeIsError = true
        })
        .finally(() => {
          this.bucketsLoading = false
        })
    },
    resetBuckets () {
      const accountId = this.bucketsAccountId
      this.bucketsLoading = true
      return this.$store.dispatch('resetRateLimitBucketsForAdmin', { accountId })
        .then((response) => {
          const data = response && response.data
          if (!data || data.status !== 'SUCCESS') throw new Error((data && data.msg) || 'reset failed')
          this.buckets = data.buckets || []
          this.notice = `${data.removed || 0} bucket(s) cleared for account ${accountId}.`
          this.noticeIsError = false
        })
        .catch((error) => {
          this.notice = this.failureText(error, 'We could not reset the buckets.')
          this.noticeIsError = true
        })
        .finally(() => {
          this.bucketsLoading = false
        })
    },
  },
}
</script>

<style lang="scss">
@import '../PopAuthority/paAdmin';

.rl-admin {
  .rl-card {
    padding: 14px 16px;
    margin-bottom: 14px;
    h3 { font-size: 15px; font-weight: 700; margin: 0; }
    .paa-tblwrap { margin-top: 10px; }
  }
  .rl-card__head {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 6px;
    .paa-muted { font-size: 12.5px; }
  }
  .rl-modes {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin: 8px 0 10px;
  }
  .rl-mode-tile {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 150px;
    padding: 10px 12px;
    border: 1px solid #e6e8ec;
    border-radius: 8px;
    .paa-muted { font-size: 12px; }
    .paa-badge { align-self: flex-start; }
  }
  .rl-row {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    margin: 8px 0;
  }
  .paa-btn--sm { padding: 5px 10px; font-size: 12px; }
  .rl-ctl--short { width: 120px; }
  .rl-ctl--spec { width: 170px; padding: 5px 8px; font-size: 12.5px; }
  .rl-tbl td { vertical-align: middle; }
  .rl-center { text-align: center; }
  .rl-actions { white-space: nowrap; .paa-btn + .paa-btn { margin-left: 6px; } }
  .rl-small { font-size: 12px; }
  .rl-barcell { min-width: 150px; white-space: nowrap; }
  .rl-bar {
    position: relative;
    display: inline-block;
    vertical-align: middle;
    width: 90px;
    height: 8px;
    margin-right: 8px;
    border-radius: 4px;
    background: #e9ecef;
    overflow: hidden;
    &--none { background: repeating-linear-gradient(45deg, #eef0f2, #eef0f2 4px, #f7f8fa 4px, #f7f8fa 8px); }
    &--warm .rl-bar__fill { background: #f59e0b; }
    &--full .rl-bar__fill { background: #dc2626; }
  }
  .rl-bar__fill {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    background: #16a34a;
    &--would { background: #f59e0b; }
    &--deny { background: #dc2626; }
  }
  .rl-num { font-variant-numeric: tabular-nums; font-size: 12.5px; vertical-align: middle; }
  .rl-hot td { background: #fff8e6; }
  .rl-mode--enforce { background: #fde8e8; color: #b42318; }
  .rl-mode--log { background: #fff4d6; color: #93520a; }
  .rl-mode--off { background: #f1f2f4; color: #6b7280; }
  .rl-notice {
    background: #e3f6e8;
    color: #166534;
    border-radius: 6px;
    padding: 10px 14px;
    margin-bottom: 12px;
    font-weight: 600;
    &--bad { background: #fde8e8; color: #b42318; }
  }
  code { font-size: 12px; background: #f6f7f9; padding: 1px 5px; border-radius: 4px; }
}
</style>
